import { 
  CRYPTO_CONFIG, 
  CryptoError, 
  EncryptedData, 
  UserProfile, 
  UserPortfolioData 
} from './lib/crypto/crypto-types.js';

export class CryptoEngine {
  private static instance: CryptoEngine;

  static getInstance(): CryptoEngine {
    if (!CryptoEngine.instance) {
      CryptoEngine.instance = new CryptoEngine();
    }
    return CryptoEngine.instance;
  }

  /**
   * Generate a random salt for key derivation
   */
  generateSalt(): Uint8Array {
    const buffer = new ArrayBuffer(CRYPTO_CONFIG.saltLength);
    const salt = new Uint8Array(buffer);
    return crypto.getRandomValues(salt);
  }

  /**
   * Generate a random IV for encryption
   */
  generateIV(): Uint8Array {
    const buffer = new ArrayBuffer(CRYPTO_CONFIG.ivLength);
    const iv = new Uint8Array(buffer);
    return crypto.getRandomValues(iv);
  }

  /**
   * Convert Uint8Array to proper BufferSource
   */
  private toBufferSource(data: Uint8Array): ArrayBuffer {
    if (data.buffer instanceof ArrayBuffer && data.buffer.byteLength === data.byteLength) {
      return data.buffer;
    }
    // If the view doesn't cover the entire buffer, copy it
    const copied = data.slice();
    return copied.buffer as ArrayBuffer;
  }

  /**
   * Derive a key from password using PBKDF2
   */
  async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);

    const baseKey = await crypto.subtle.importKey(
      'raw',
      passwordBuffer,
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: this.toBufferSource(salt),
        iterations: CRYPTO_CONFIG.pbkdf2Iterations,
        hash: 'SHA-256',
      },
      baseKey,
      {
        name: CRYPTO_CONFIG.algorithm,
        length: CRYPTO_CONFIG.keyLength,
      },
      false, // Non-extractable
      ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
    );
  }

  /**
   * Generate a new data key for encryption
   */
  async generateDataKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(
      {
        name: CRYPTO_CONFIG.algorithm,
        length: CRYPTO_CONFIG.keyLength,
      },
      false, // Non-extractable
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Wrap a data key with a master key
   */
  async wrapKey(dataKey: CryptoKey, masterKey: CryptoKey): Promise<Uint8Array> {
    const iv = this.generateIV();
    const wrappedKey = await crypto.subtle.wrapKey(
      'raw',
      dataKey,
      masterKey,
      {
        name: CRYPTO_CONFIG.algorithm,
        iv: this.toBufferSource(iv),
      }
    );
    // Combine IV + wrapped key for storage
    const combined = new Uint8Array(iv.length + wrappedKey.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(wrappedKey), iv.length);
    return combined;
  }

  /**
   * Unwrap a data key with a master key
   */
  async unwrapKey(wrappedKey: Uint8Array, masterKey: CryptoKey, iv: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.unwrapKey(
      'raw',
      this.toBufferSource(wrappedKey),
      masterKey,
      {
        name: CRYPTO_CONFIG.algorithm,
        iv: this.toBufferSource(iv),
      },
      {
        name: CRYPTO_CONFIG.algorithm,
        length: CRYPTO_CONFIG.keyLength,
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data with AES-GCM
   */
  async encrypt(data: string, key: CryptoKey): Promise<EncryptedData> {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const iv = this.generateIV();

    const ciphertext = await crypto.subtle.encrypt(
      {
        name: CRYPTO_CONFIG.algorithm,
        iv: this.toBufferSource(iv),
      },
      key,
      dataBuffer
    );

    return {
      ciphertext: new Uint8Array(ciphertext),
      iv: iv,
    };
  }

  /**
   * Decrypt data with AES-GCM
   */
  async decrypt(encryptedData: EncryptedData, key: CryptoKey): Promise<string> {
    try {
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: CRYPTO_CONFIG.algorithm,
          iv: this.toBufferSource(encryptedData.iv),
        },
        key,
        this.toBufferSource(encryptedData.ciphertext)
      );

      const decoder = new TextDecoder();
      return decoder.decode(decryptedBuffer);
    } catch (error) {
      throw new CryptoError('Decryption failed - invalid key or corrupted data', 'DECRYPT_ERROR');
    }
  }

  /**
   * Hash password for verification (not for key derivation)
   */
  async hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const encoder = new TextEncoder();
    const passwordBuffer = encoder.encode(password);
    
    const combined = new Uint8Array(passwordBuffer.length + salt.length);
    combined.set(passwordBuffer);
    combined.set(salt, passwordBuffer.length);
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
    
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Verify password against hash
   */
  async verifyPassword(password: string, hash: string, salt: Uint8Array): Promise<boolean> {
    const computedHash = await this.hashPassword(password, salt);
    return computedHash === hash;
  }

  /**
   * Generate a unique user ID
   */
  generateUserId(): string {
    const buffer = new ArrayBuffer(16);
    const array = new Uint8Array(buffer);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Create a new user profile with encrypted master key
   */
  async createUserProfile(username: string, password: string): Promise<UserProfile> {
    const salt = this.generateSalt();
    const masterKey = await this.deriveKey(password, salt);
    const dataKey = await this.generateDataKey();
    const wrappedMasterKey = await this.wrapKey(dataKey, masterKey);
    const passwordHash = await this.hashPassword(password, salt);

    return {
      id: this.generateUserId(),
      username,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      loginCount: 1,
      passwordHash,
      salt,
      wrappedMasterKey,
    };
  }

  /**
   * Authenticate user and return unwrapped session key
   */
  async authenticateUser(userProfile: UserProfile, password: string): Promise<CryptoKey> {
    const isValid = await this.verifyPassword(password, userProfile.passwordHash, userProfile.salt);
    if (!isValid) {
      throw new CryptoError('Invalid password', 'AUTH_ERROR');
    }

    const masterKey = await this.deriveKey(password, userProfile.salt);
    
    // Extract IV from wrapped key (first 12 bytes) and actual wrapped key
    const iv = userProfile.wrappedMasterKey.slice(0, CRYPTO_CONFIG.ivLength);
    const wrappedKey = userProfile.wrappedMasterKey.slice(CRYPTO_CONFIG.ivLength);
    
    return this.unwrapKey(wrappedKey, masterKey, iv);
  }

  /**
   * Encrypt portfolio data for storage
   */
  async encryptPortfolioData(portfolioData: any, sessionKey: CryptoKey, userId: string): Promise<UserPortfolioData> {
    const jsonData = JSON.stringify(portfolioData);
    const encryptedPortfolio = await this.encrypt(jsonData, sessionKey);

    return {
      userId,
      version: 1,
      encryptedPortfolio,
      lastModified: new Date().toISOString(),
    };
  }

  /**
   * Decrypt portfolio data from storage
   */
  async decryptPortfolioData(encryptedData: UserPortfolioData, sessionKey: CryptoKey): Promise<any> {
    const decryptedJson = await this.decrypt(encryptedData.encryptedPortfolio, sessionKey);
    return JSON.parse(decryptedJson);
  }

  /**
   * Secure memory cleanup
   */
  secureCleanup(sensitiveData: any): void {
    if (sensitiveData && typeof sensitiveData === 'object') {
      Object.keys(sensitiveData).forEach(key => {
        if (sensitiveData[key] instanceof Uint8Array) {
          sensitiveData[key].fill(0);
        }
        delete sensitiveData[key];
      });
    }
  }
}
